/**
 * EventInvitations — the invitation surface for a donor event.
 *
 * Invitations used to be a single name+email row that wrote an `event_attendees`
 * record and sent nothing; "Invited 40 guests" meant "wrote 40 rows". This panel
 * is built around the distinction the backend now reports: `invited` (roster rows)
 * versus `notified` (people actually reached), with the delivery channel chosen
 * explicitly and failures surfaced per guest instead of swallowed.
 */

import React, { useEffect, useState } from 'react';
import { AlertTriangle, Loader2, Mail, Send, UserPlus, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DeliveryResult,
  DonorEvent,
  InviteChannel,
  broadcastDonorEvent,
  inviteCohortToThisEvent,
  inviteToDonorEvent,
} from '@/services/api/donorEvents';
import { DonorCohort, listCohorts } from '@/services/api/donorCohorts';
import DonorPeoplePicker from '@/components/donors/DonorPeoplePicker';

const CHANNELS: { id: InviteChannel; label: string }[] = [
  { id: 'email', label: 'Email' },
  { id: 'sms', label: 'SMS' },
  { id: 'portal', label: 'Portal' },
];

function DeliverySummary({ result }: { result: DeliveryResult }) {
  const failures = result.failures || [];
  return (
    <div className="rounded border border-gray-200 bg-gray-50 p-3 text-sm dark:border-gray-700 dark:bg-gray-800">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        {result.invited != null ? (
          <span className="text-gray-600 dark:text-gray-300">
            <strong>{result.invited}</strong> added to the guest list
          </span>
        ) : null}
        <span className="font-medium text-green-700 dark:text-green-400">
          <strong>{result.notified}</strong> notified
        </span>
        {result.email ? <span className="text-xs text-gray-500">{result.email} email</span> : null}
        {result.sms ? <span className="text-xs text-gray-500">{result.sms} SMS</span> : null}
        {result.portal ? <span className="text-xs text-gray-500">{result.portal} portal</span> : null}
      </div>
      {failures.length > 0 ? (
        <div className="mt-2 space-y-1 border-t border-gray-200 pt-2 dark:border-gray-700">
          <div className="flex items-center gap-1.5 text-xs font-medium text-amber-700 dark:text-amber-400">
            <AlertTriangle size={13} /> {failures.length} could not be reached
          </div>
          {failures.slice(0, 5).map((f, i) => (
            <div key={i} className="text-xs text-gray-500">
              {f.name || 'Guest'} — {f.reason}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export const EventInvitations: React.FC<{ event: DonorEvent; onChanged: () => void }> = ({ event, onChanged }) => {
  const [channels, setChannels] = useState<InviteChannel[]>(['email']);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [cohorts, setCohorts] = useState<DonorCohort[]>([]);
  const [cohortId, setCohortId] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [result, setResult] = useState<DeliveryResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [guestPhone, setGuestPhone] = useState('');

  useEffect(() => {
    listCohorts()
      .then(setCohorts)
      .catch(() => setCohorts([]));
  }, []);

  const toggleChannel = (c: InviteChannel) =>
    setChannels((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]));

  const run = async (key: string, fn: () => Promise<DeliveryResult>) => {
    setBusy(key);
    setError(null);
    setResult(null);
    try {
      setResult(await fn());
      onChanged();
    } catch (e: any) {
      setError(e?.message || 'Something went wrong sending the invitations.');
    } finally {
      setBusy(null);
    }
  };

  const copy = { subject: subject.trim() || undefined, body: body.trim() || undefined };
  const field =
    'w-full rounded border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100';

  const notNotified = Math.max(0, (event.invited || 0) - (event.notified || 0));

  return (
    <div className="space-y-5">
      <div className="grid gap-3 sm:grid-cols-4">
        {[
          { label: 'On the guest list', value: event.invited },
          { label: 'Notified', value: event.notified, tone: 'text-green-700 dark:text-green-400' },
          { label: 'Accepted', value: event.accepted },
          { label: 'Declined', value: event.declined },
        ].map((s) => (
          <div key={s.label} className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
            <div className={`text-xl font-semibold ${s.tone || 'text-gray-900 dark:text-gray-100'}`}>{s.value ?? 0}</div>
            <div className="text-xs text-gray-500">{s.label}</div>
          </div>
        ))}
      </div>

      {notNotified > 0 ? (
        <div className="flex flex-wrap items-center gap-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-900 dark:bg-amber-900/20 dark:text-amber-300">
          <AlertTriangle size={15} />
          <span>
            {notNotified} guest{notNotified === 1 ? '' : 's'} on the list {notNotified === 1 ? 'has' : 'have'} never been
            sent an invitation.
          </span>
          <Button
            size="sm"
            variant="outline"
            disabled={busy !== null}
            onClick={() => run('unnotified', () => broadcastDonorEvent(event.id, { kind: 'invite', only_unnotified: true, channels, ...copy }))}
          >
            {busy === 'unnotified' ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Send size={13} className="mr-1" />}
            Send to them
          </Button>
        </div>
      ) : null}

      {error ? (
        <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-900/20 dark:text-red-300">
          {error}
        </div>
      ) : null}
      {result ? <DeliverySummary result={result} /> : null}

      <section className="space-y-3 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
          <Mail size={15} /> Message
        </h3>
        <div className="flex flex-wrap items-center gap-4">
          <span className="text-xs font-medium text-gray-500">Send via</span>
          {CHANNELS.map((c) => (
            <label key={c.id} className="flex items-center gap-1.5 text-sm text-gray-700 dark:text-gray-300">
              <input type="checkbox" checked={channels.includes(c.id)} onChange={() => toggleChannel(c.id)} />
              {c.label}
            </label>
          ))}
        </div>
        <input
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          className={field}
          placeholder="Subject (optional — a default invitation is used)"
        />
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={4}
          className={field}
          placeholder="Message (optional). The default includes the date, venue and an RSVP link."
        />
        <p className="text-xs text-gray-400">
          Email invitations include a calendar invite (.ics) and the public RSVP link.
        </p>
      </section>

      <section className="space-y-3 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
          <Users size={15} /> Invite a cohort
        </h3>
        <div className="flex flex-wrap items-center gap-2">
          <select value={cohortId} onChange={(e) => setCohortId(e.target.value)} className={`${field} flex-1`}>
            <option value="">Choose a donor cohort…</option>
            {cohorts.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} ({c.member_count})
              </option>
            ))}
          </select>
          <Button
            disabled={!cohortId || busy !== null || channels.length === 0}
            onClick={() => run('cohort', () => inviteCohortToThisEvent(event.id, Number(cohortId), { channels, ...copy }))}
          >
            {busy === 'cohort' ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Send size={14} className="mr-1" />}
            Invite cohort
          </Button>
        </div>
        {cohorts.length === 0 ? (
          <p className="text-xs text-gray-400">No cohorts yet — build one on the Donor cohorts page.</p>
        ) : null}
      </section>

      <section className="space-y-3 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
          <UserPlus size={15} /> Invite from your records
        </h3>
        <DonorPeoplePicker
          addLabel="Invite selected"
          onAdd={async (people) => {
            let notified = 0;
            let invited = 0;
            const failures: { name?: string | null; reason: string }[] = [];
            for (const p of people) {
              try {
                const r = await inviteToDonorEvent(event.id, { member_id: p.id, channels, ...copy });
                notified += r.notified || 0;
                invited += r.invited || 0;
                (r.failures || []).forEach((f) => failures.push(f));
              } catch (e: any) {
                failures.push({ name: p.name, reason: e?.message || 'Invite failed' });
              }
            }
            setResult({ invited, notified, failures });
            onChanged();
          }}
        />
      </section>

      <section className="space-y-3 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Invite an outside guest</h3>
        <div className="grid gap-2 sm:grid-cols-3">
          <input value={guestName} onChange={(e) => setGuestName(e.target.value)} placeholder="Name" className={field} />
          <input value={guestEmail} onChange={(e) => setGuestEmail(e.target.value)} placeholder="email@example.com" className={field} />
          <input value={guestPhone} onChange={(e) => setGuestPhone(e.target.value)} placeholder="Mobile (for SMS)" className={field} />
        </div>
        <Button
          size="sm"
          disabled={(!guestEmail.trim() && !guestPhone.trim()) || busy !== null}
          onClick={() =>
            run('guest', async () => {
              const r = await inviteToDonorEvent(event.id, {
                external_email: guestEmail.trim() || undefined,
                external_name: guestName.trim() || undefined,
                external_phone: guestPhone.trim() || undefined,
                channels,
                ...copy,
              });
              setGuestName('');
              setGuestEmail('');
              setGuestPhone('');
              return r;
            })
          }
        >
          {busy === 'guest' ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Send size={14} className="mr-1" />}
          Send invitation
        </Button>
      </section>

      <section className="space-y-3 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Follow up</h3>
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="outline"
            disabled={busy !== null}
            onClick={() => run('remind', () => broadcastDonorEvent(event.id, { kind: 'reminder', channels, ...copy }))}
          >
            {busy === 'remind' ? <Loader2 size={13} className="mr-1 animate-spin" /> : null}
            Send a reminder to everyone
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={busy !== null}
            onClick={() =>
              run('remind-accepted', () =>
                broadcastDonorEvent(event.id, { kind: 'reminder', response_statuses: ['accepted'], channels, ...copy }),
              )
            }
          >
            Remind those who accepted
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={busy !== null}
            onClick={() => run('thanks', () => broadcastDonorEvent(event.id, { kind: 'thanks', attended_only: true, channels, ...copy }))}
          >
            Thank everyone who attended
          </Button>
        </div>
      </section>
    </div>
  );
};

export default EventInvitations;
